import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, naira, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Copy, MapPin, Phone, IdCard, History, Smartphone } from "lucide-react";
import { UssdSimulator } from "@/components/UssdSimulator";
import { ShareStory } from "@/components/ShareStory";
import { NearbyShops } from "@/components/NearbyShops";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function BeneficiaryPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/beneficiaries/me").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  const { profile, vouchers, transactions } = data;
  const statusColor = { active: "bg-emerald-100 text-emerald-800", fully_redeemed: "bg-gray-200 text-gray-700", expired: "bg-red-100 text-red-700" };

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Beneficiary Portal</span>
        <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950">Hello{profile.full_name ? `, ${profile.full_name}` : ""}</h1>

        {/* Profile card */}
        <div className="mt-6 rounded-3xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white p-6 lg:p-8 relative overflow-hidden">
          <IdCard className="w-40 h-40 absolute -right-6 -bottom-8 text-white/10" />
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-200">My TMF Profile</div>
          <div className="mt-3 flex items-center gap-2">
            <span data-testid="beneficiary-tmf-id" className="font-mono font-bold text-2xl">{profile.tmf_id}</span>
            <button onClick={() => { navigator.clipboard.writeText(profile.tmf_id); toast.success("Copied"); }} className="p-1.5 rounded hover:bg-white/10"><Copy className="w-4 h-4" /></button>
          </div>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-emerald-50">
            <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" /> {profile.phone}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Preferred: {profile.preferred_state || "—"}</span>
            <span className="uppercase text-xs bg-white/15 px-2 py-0.5 rounded">{profile.benefit_pref}</span>
          </div>
        </div>

        {/* Active benefits */}
        <h2 className="mt-10 font-display font-bold text-xl text-emerald-950">Active Benefits</h2>
        <div className="mt-4 rounded-2xl border border-emerald-100 bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-emerald-50/60">
              <TableHead>Programme</TableHead><TableHead>Voucher ID</TableHead><TableHead>Original</TableHead>
              <TableHead>Used</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {vouchers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-10">No benefits yet. A sponsor will allocate your voucher soon.</TableCell></TableRow>}
              {vouchers.map((v) => (
                <TableRow key={v.id} data-testid={`benefit-row-${v.code}`}>
                  <TableCell className="font-semibold">{v.programme_code}</TableCell>
                  <TableCell className="font-mono text-xs text-emerald-700">{v.tmf_voucher_id || v.code}</TableCell>
                  <TableCell>{naira(v.original_value)}</TableCell>
                  <TableCell className="text-gray-500">{naira(v.used)}</TableCell>
                  <TableCell className="font-bold text-emerald-700">{naira(v.balance)}</TableCell>
                  <TableCell><Badge className={statusColor[v.status] || ""}>{v.status.replace("_", " ")}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* History */}
        <h2 className="mt-10 font-display font-bold text-xl text-emerald-950 flex items-center gap-2"><History className="w-5 h-5" /> Redemption History</h2>
        <div className="mt-4 space-y-3">
          {transactions.length === 0 && <p className="text-gray-500 text-sm">No transactions yet.</p>}
          {transactions.map((t) => (
            <div key={t.id} data-testid={`txn-${t.txn_id}`} className="rounded-xl border border-emerald-100 bg-white p-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-xs text-gray-400">{t.txn_id}</div>
                <div className="text-sm font-medium text-emerald-950">{t.type === "reversal" ? "Reversal" : "Redemption"} · {t.vendor_name}</div>
                <div className="text-xs text-gray-500">{new Date(t.timestamp).toLocaleString()}</div>
              </div>
              <div className="text-right">
                <div className={`font-bold ${t.type === "reversal" ? "text-blue-600" : "text-red-600"}`}>{t.type === "reversal" ? "+" : "−"}{naira(t.amount)}</div>
                <div className="text-xs text-gray-500">Bal: {naira(t.new_balance)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl bg-emerald-950 p-6 text-emerald-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-400">USSD Access (DEMO)</div>
            <p className="mt-2 text-sm">No smartphone? Dial <span className="font-mono font-bold text-white">*7006*99#</span> → 1. Redeem Food → 2. Cash Out → 3. Check Benefit → 4. Find Vendor.</p>
            <button onClick={() => navigate("/find-vendor")} className="mt-3 text-sm font-semibold text-emerald-300 underline">Find a vendor near you →</button>
          </div>
          <UssdSimulator trigger={
            <Button data-testid="beneficiary-ussd-btn" className="bg-emerald-600 hover:bg-emerald-500 rounded-full h-11 px-5 shrink-0"><Smartphone className="w-4 h-4 mr-2" /> Try *7006*99#</Button>
          } />
        </div>
        <NearbyShops state={profile?.preferred_state} />
        <ShareStory defaultName={user?.full_name} />
      </div>
    </div>
  );
}
